import { useCallback, useEffect, useMemo, useState } from 'react'

import type { FreeTrial as GuestPass } from '@mc/api'

import { usePrimaryCta } from '@mc/growth'
import { getPageSource, NavigationEvents, track } from '@mc/track-event'
import { useStaticData } from '@mc/static-data-react'

import GuestPassStickyFooter from 'components/GuestPassStickyFooter'
import Sticky from 'components/Sticky'
import {
  ConciergeExperienceExperiment,
  useTrackConciergeExperienceExperiment,
} from 'experiments/pen-4910_v1_concierge_experience'

import { StickyContent } from './StickyContent'
import { useConfig } from './useConfig'

interface StickyFooterProps {
  customHref?: string
  trackProps?: Record<string, string>
}

export const StickyFooter = ({
  customHref,
  trackProps = {},
}: StickyFooterProps) => {
  const { guestPass } = useStaticData<{ guestPass: GuestPass }>()
  const { config } = useConfig()
  const { ctaCopy } = usePrimaryCta()
  const [pageSourceTrack, setPageSourceTrack] = useState<string>()

  useTrackConciergeExperienceExperiment()

  const trackData = useMemo(
    () => ({
      content_type: 'product',
      location: 'sticky_footer',
      page_source: pageSourceTrack,
      ...trackProps,
    }),
    [trackProps, pageSourceTrack],
  )

  useEffect(() => {
    setPageSourceTrack(getPageSource())
  }, [])

  const trackNavigationClicked = useCallback(
    (action: string) =>
      track(NavigationEvents.EVENTS.NAVIGATION_CLICKED, {
        action,
        location: trackData.location,
        page_source: trackData.page_source,
      }),
    [trackData],
  )

  if (guestPass?.advocate) {
    return <GuestPassStickyFooter />
  }

  return (
    <>
      <ConciergeExperienceExperiment />
      <Sticky config={config}>
        <div id='sticky-footer' data-testid='sticky-footer'>
          <StickyContent
            ctaProps={{
              href: customHref,
              trackProps: trackData,
              onClick: trackNavigationClicked,
              children: ctaCopy,
            }}
          />
        </div>
      </Sticky>
    </>
  )
}
